import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane, Input, Button, Row, Col, Spinner } from 'reactstrap';
import classnames from 'classnames';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { Link } from 'react-router-dom';
import { getInspectorsByType, assignInspectorToPropertyUnit, updateInspectorOfPropertyUnitInspection } from 'endpoints/builder/builderPropertyListingDetails';
import profile_placeholder from "assets/images/profile_placeholder.png";
import { toast } from 'react-toastify';
import { RootState } from 'slices/store';

interface AssignInspectorModalProps {
  isOpen: boolean;
  toggle: () => void;
  setInspectionId: (id: string) => void;
  inspectionId?: string;
}

interface Inspector {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  street_address?: string;
  city?: string;
  status: string;
  createdAt: string;
  profile_image?: string;
}

const AssignInspectorModal: React.FC<AssignInspectorModalProps> = ({
  isOpen,
  toggle,
  setInspectionId,
  inspectionId
}) => {
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('1'); // '1' for Company, '2' for External Inspector
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [assigningInspectorId, setAssigningInspectorId] = useState<string | null>(null);
  const limit = 4;

  // Get property unit ID from Redux store
  const propertyUnitId = useSelector((state: RootState) => state.Builder.selectedPropertyUnitId);

  // Determine inspector type based on active tab
  const inspectorType = activeTab === '1' ? 'company' : 'external';

  // Debounced search function
  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 500)
  ).current;

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };

  // Create filters for API call
  const filters = useMemo(() => {
    const baseFilters = {
      status: 'accepted',
      inspectorType,
      page: currentPage,
      limit
    };

    // Only add search if there's a search term
    if (searchTerm && searchTerm.trim() !== '') {
      return {
        ...baseFilters,
        search: searchTerm.trim()
      };
    }

    return baseFilters;
  }, [inspectorType, searchTerm, currentPage]);

  // API call
  const {
    data: inspectorsData,
    isLoading,
    isError,
    refetch,
    isFetching
  } = useQuery({
    queryKey: ['inspectors', filters],
    queryFn: () => getInspectorsByType(filters),
    enabled: isOpen, // Only fetch when modal is open
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    retry: 3,
  });

  // Create inspection mutation
  const createInspectionMutation = useMutation({
    mutationFn: (data: {
      inspection_type: string;
      inspection_amount: number;
      inspector_id: string;
      property_unit_id: string;
    }) => assignInspectorToPropertyUnit(data),
    onSuccess: (response) => {

      if (response?.status_code === 200) {
        if (response?.data?.id) {
          setInspectionId(response?.data?.id);
          if (response?.data?.inspection_type === 'primary') {
            // Invalidate related queries to ensure fresh data
            queryClient.invalidateQueries({
              queryKey: ['propertyUnitInspectionDetails']
            });
          } else {
            queryClient.invalidateQueries({
              queryKey: ['propertyUnitSecondaryInspectionDetails']
            });
          }

          // Clear the assigning inspector ID
          setAssigningInspectorId(null);
          toggle(); // Close modal after successful assignment
        }
        toast.success('Inspector assigned successfully!');
      }
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to assign inspector');
      console.error('Create inspection error:', error);

      // Clear the assigning inspector ID on error
      setAssigningInspectorId(null);
    }
  });

  // Update inspection mutation
  const updateInspectionMutation = useMutation({
    mutationFn: (data: {
      inspection_id: string;
      inspector_id: string;
      status: string;
    }) => updateInspectorOfPropertyUnitInspection(data.inspection_id, {
      inspector_id: data.inspector_id,
      status: data.status
    }),
    onSuccess: (response) => {
      if (response?.status_code === 400) {
        toast.error(response?.response || 'Failed to update inspector');
        // Clear the assigning inspector ID on error
        setAssigningInspectorId(null);
        return;
      }
      if (response?.status_code === 200) {

         if (response?.data?.id ) {
          setInspectionId(response?.data?.id);
          if (response?.data?.inspection_type === 'primary') {
            // Invalidate related queries to ensure fresh data
            queryClient.invalidateQueries({
              queryKey: ['propertyUnitInspectionDetails']
            });
          }else {
            queryClient.invalidateQueries({
              queryKey: ['propertyUnitSecondaryInspectionDetails']
            });
          }

          // Clear the assigning inspector ID
          setAssigningInspectorId(null);
          toggle(); // Close modal after successful assignment
        }
        toast.success('Inspector updated successfully!');
      }
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update inspector');
      console.error('Update inspection error:', error);

      // Clear the assigning inspector ID on error
      setAssigningInspectorId(null);
    }
  });

  // Combined loading state that includes mutations
  const isLoadingOrFetching = isLoading || isFetching;

  // Extract data from API response
  const inspectors = inspectorsData?.data?.inspectors || [];
  const totalCount = inspectorsData?.data?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Reset page when tab changes or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  // Reset to tab 1 when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('1');
      setSearchInput('');
      setSearchTerm('');
      setCurrentPage(1);
      setAssigningInspectorId(null); // Reset assigning state
    }
  }, [isOpen]);

  const handleAssign = (inspectorId: string) => {
    // Set the inspector being assigned
    setAssigningInspectorId(inspectorId);

    // Determine inspection type based on active tab
    const inspectionType = activeTab === '1' ? 'primary' : 'secondary';

    if (inspectionId) {
      // Update existing inspection
      updateInspectionMutation.mutate({
        inspection_id: inspectionId,
        inspector_id: inspectorId,
        status: 'assigned'
      });
    } else {
      // Create new inspection
      createInspectionMutation.mutate({
        inspection_type: inspectionType,
        inspection_amount: 100,
        inspector_id: inspectorId,
        property_unit_id: propertyUnitId
      });
    }
  };

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setSearchInput(''); // Reset search when changing tabs
      setSearchTerm(''); // Reset search term immediately
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Create pagination info outside of the function to avoid closure issues
  const paginationInfo = useMemo(() => {
    if (totalPages <= 0 || totalCount <= 0) return null;

    return {
      startEntry: (currentPage - 1) * limit + 1,
      endEntry: Math.min(currentPage * limit, totalCount),
      totalEntries: totalCount,
      canPrevious: currentPage > 1,
      canNext: currentPage < totalPages,
      currentPageIndex: currentPage - 1, // 0-based for consistency with TableContainer
      pageOptions: Array.from({ length: totalPages }, (_, i) => i)
    };
  }, [totalCount, totalPages, currentPage, limit]);

  // Pagination component matching TableContainer pattern
  const renderPagination = () => {
    if (!paginationInfo) return null;

    const handlePreviousPage = () => {
      if (currentPage > 1) {
        handlePageChange(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        handlePageChange(currentPage + 1);
      }
    };

    const handlePageClick = (pageIndex: number) => {
      handlePageChange(pageIndex + 1); // Convert from 0-based to 1-based
    };

    return (
      <Row className="align-items-center mt-3 g-3 text-center text-sm-start">
        <div className="col-sm">
          <div className="text-muted">
            {paginationInfo.totalEntries > 0 ? (
              <>
                Showing <span className="fw-semibold">{paginationInfo.startEntry}</span> to <span className="fw-semibold">{paginationInfo.endEntry}</span> of <span className="fw-semibold">{paginationInfo.totalEntries}</span> Results
              </>
            ) : (
              <span className="fw-semibold">No Results</span>
            )}
          </div>
        </div>
        <div className="col-sm-auto">
          <ul className="pagination pagination-separated pagination-sm justify-content-center justify-content-sm-start mb-0">
            <li className={!paginationInfo.canPrevious ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); handlePreviousPage(); }}>Previous</Link>
            </li>
            {(() => {
              const totalPagesCount = paginationInfo.pageOptions.length;
              const currentPageIndex = paginationInfo.currentPageIndex;
              const delta = 1; // Number of pages to show before and after current page

              let pages = [];

              // Always show first page
              pages.push(0);

              // Calculate range around current page
              let rangeStart = Math.max(1, currentPageIndex - delta);
              let rangeEnd = Math.min(totalPagesCount - 2, currentPageIndex + delta);

              // Adjust range if current page is near start or end
              if (currentPageIndex <= delta) {
                rangeEnd = Math.min(2 * delta + 1, totalPagesCount - 2);
              }
              if (currentPageIndex >= totalPagesCount - delta - 1) {
                rangeStart = Math.max(totalPagesCount - 2 * delta - 2, 1);
              }

              // Add ellipsis after first page if needed
              if (rangeStart > 1) {
                pages.push(-1); // -1 represents ellipsis
              }

              // Add pages in range
              for (let i = rangeStart; i <= rangeEnd; i++) {
                pages.push(i);
              }

              // Add ellipsis before last page if needed
              if (rangeEnd < totalPagesCount - 2) {
                pages.push(-2); // -2 represents ellipsis
              }

              // Always show last page if we have more than one page
              if (totalPagesCount > 1) {
                pages.push(totalPagesCount - 1);
              }

              return pages.map((pageIndex, key) => (
                <React.Fragment key={key}>
                  {pageIndex >= 0 ? (
                    <li className="page-item">
                      <Link
                        to="#"
                        className={paginationInfo.currentPageIndex === pageIndex ? "page-link active" : "page-link"}
                        onClick={(e) => { e.preventDefault(); handlePageClick(pageIndex); }}
                      >
                        {pageIndex + 1}
                      </Link>
                    </li>
                  ) : (
                    <li className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  )}
                </React.Fragment>
              ));
            })()}
            <li className={!paginationInfo.canNext ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); handleNextPage(); }}>Next</Link>
            </li>
          </ul>
        </div>
      </Row>
    );
  };

  // Render inspector item
  const renderInspectorItem = (inspector: Inspector) => (
    <div key={inspector.id} className="py-2 px-3 border rounded mb-3">
      <Row className="d-flex align-items-center g-0">
        <Col xs={9}>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <div className="rounded-circle bg-light d-flex justify-content-center align-items-center" style={{ width: '50px', height: '50px', overflow: 'hidden' }}>
              <img
                src={inspector?.profile_image || profile_placeholder}
                alt={`${inspector?.first_name} ${inspector?.last_name}`}
                className="rounded-circle"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div>
              <h6 className="mb-0">{inspector.first_name} {inspector.last_name}</h6>
              <p className="text-muted mb-0 mt-0" style={{ lineHeight: '1' }}>
                <small>{inspector.email}</small>
              </p>
              {inspector.city && (
                <p className="text-muted mb-0 mt-0" style={{ lineHeight: '1' }}>
                  <small>{inspector.city}</small>
                </p>
              )}
            </div>
          </div>
        </Col>
        <Col xs={3} className="text-end">
          <Button
            color="light"
            onClick={() => handleAssign(inspector.id)}
            disabled={assigningInspectorId === inspector.id}
          >
            {assigningInspectorId === inspector.id ? (
              <span className="d-flex align-items-center">
                <Spinner size="sm" className="me-1" />
                Assigning...
              </span>
            ) : (
              'Assign'
            )}
          </Button>
        </Col>
      </Row>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="md">
      <ModalHeader toggle={toggle}>Assign Inspector</ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <label className="form-label">Location</label>
          <Input
            type="text"
            placeholder="Search by Location..."
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
        <hr className="mb-4 mt-4" />

        <h6 className="mb-3">Select Inspector</h6>

        <Nav tabs className="nav nav-tabs nav-tabs-custom nav-success nav-justified mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '1' })}
              style={{ cursor: 'pointer' }}
              onClick={() => toggleTab('1')}
            >
              Company Inspector
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === '2' })}
              style={{ cursor: 'pointer' }}
              onClick={() => toggleTab('2')}
            >
              External Inspector
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab} key={`tab-content-${activeTab}-${inspectorType}`}>
          <TabPane tabId="1">
            {isLoadingOrFetching ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading company inspectors...</p>
              </div>
            ) : isError ? (
              <div className="text-center py-5">
                <div className="text-danger mb-3">
                  <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-danger">Failed to load inspectors</h5>
                <p className="text-muted">Please check your connection and try again.</p>
                <Button color="primary" onClick={() => refetch()}>
                  <i className="ri-refresh-line me-1"></i>
                  Retry
                </Button>
              </div>
            ) : inspectors.length === 0 ? (
              <div className="text-center py-5">
                <div className="text-muted mb-3">
                  <i className="ri-search-line" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-muted">No company inspectors found</h5>
                {/* <p className="text-muted">
                  {searchTerm ? 'Try adjusting your search criteria.' : 'No company inspectors available.'}
                </p> */}
              </div>
            ) : (
              <>
                {inspectors.map(renderInspectorItem)}
                {renderPagination()}
              </>
            )}
          </TabPane>
          <TabPane tabId="2">
            {isLoadingOrFetching ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading external inspectors...</p>
              </div>
            ) : isError ? (
              <div className="text-center py-5">
                <div className="text-danger mb-3">
                  <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-danger">Failed to load inspectors</h5>
                <p className="text-muted">Please check your connection and try again.</p>
                <Button color="primary" onClick={() => refetch()}>
                  <i className="ri-refresh-line me-1"></i>
                  Retry
                </Button>
              </div>
            ) : inspectors.length === 0 ? (
              <div className="text-center py-5">
                <div className="text-muted mb-3">
                  <i className="ri-search-line" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-muted">No external inspectors found</h5>
                {/* <p className="text-muted">
                  {searchTerm ? 'Try adjusting your search criteria.' : 'No external inspectors available.'}
                </p> */}
              </div>
            ) : (
              <>
                {inspectors.map(renderInspectorItem)}
                {renderPagination()}
              </>
            )}
          </TabPane>
        </TabContent>

        {/* <div className="d-flex justify-content-end mt-4">
          <Button color="danger" onClick={toggle}>Cancel</Button>
        </div> */}
      </ModalBody>
    </Modal>
  );
};

export default AssignInspectorModal; 